// Copyright ©️ 2026 eVolve MEP, LLC

import type { MachineElement, StrongOmit } from '@evolvemep/api-ts';
import { Stack, Text, Title } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import {
  BendQuickView,
  type BendQuickViewProps,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendQuickView';
import { isNotNilOrEmptyString } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { QuickViewAndModelPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/QuickViewAndModelPreview';

type Props = {
  element?: MachineElement;
} & BendQuickViewProps;

export const betaFeatureText = <Text c="dimmed">Beta feature. Bend preview is an approximation.</Text>;

const hasElementWithModelData = (element?: MachineElement): element is MachineElement =>
  isNotNil(element) && (isNotNilOrEmptyString(element.spool) || isNotNilOrEmptyString(element.sourceIds));

const QuickViewOnlyPreview = ({ bendType, ...rest }: StrongOmit<Props, 'element'>) => (
  <Stack flex={1} gap={0} miw="100%" h="100%" pos="relative">
    <Stack gap={0} pos="absolute" top={0} left={0}>
      <Title order={5}>{bendType}</Title>
      {betaFeatureText}
    </Stack>
    <BendQuickView bendType={bendType} {...rest} />
  </Stack>
);

export const BendPreview = ({ element, ...rest }: Props) =>
  hasElementWithModelData(element) ? (
    <QuickViewAndModelPreview element={element} {...rest} />
  ) : (
    <QuickViewOnlyPreview {...rest} />
  );
